// components/Hero.js
import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import heroVideo from "../assets/code1.mp4";

gsap.registerPlugin(ScrollTrigger);

const focusAreas = [
  "Full-stack software engineer",
  "Cybersecurity engineer",
  "Founder",
];

const toolMarks = [
  "JavaScript",
  "React",
  "Node.js",
  "Python",
  "React Native",
  "Cybersecurity",
  "MERN Stack",
  "MongoDB",
  "Express.js",
  "PostgreSQL",
  "Redux",
  "Redis",
  "LiveKit",
];

const Hero = () => {
  const heroRef = useRef(null);
  const heroContentRef = useRef(null);
  const heroVideoRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (prefersReducedMotion) return;

      gsap
        .timeline({
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        })
        .to(
          heroContentRef.current,
          {
            y: -90,
            opacity: 0.18,
            scale: 0.98,
            ease: "none",
          },
          0
        )
        .to(
          heroVideoRef.current,
          {
            scale: 1.08,
            ease: "none",
          },
          0
        );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-[100svh] overflow-hidden bg-[#f4f3ee] text-[#242424] dark:bg-black dark:text-[#f4f3ee]"
    >
      <video
        ref={heroVideoRef}
        className="absolute inset-0 h-full w-full object-cover opacity-[0.16] grayscale contrast-125 will-change-transform dark:opacity-[0.2]"
        src={heroVideo}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      />

      <div className="absolute inset-0 bg-[#f4f3ee]/82 dark:bg-black/78" />

      <div
        ref={heroContentRef}
        className="container relative z-10 mx-auto flex min-h-[100svh] flex-col justify-between px-6 pb-32 pt-28 will-change-transform sm:pb-28 md:pb-36 md:pt-32"
      >
        <div className="flex flex-1 items-center justify-center text-center">
          <div>
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#6f6b63] dark:text-[#b9b4aa]">
              <span className="text-[var(--brand-blue)]">William</span>{" "}
              Writes{" "}
              <span className="text-[var(--brand-green)]">Code</span>
            </p>

            <h1 className="max-w-6xl text-[clamp(3.4rem,12vw,10.75rem)] font-normal leading-[0.82] tracking-[-0.078em] text-[var(--brand-navy)] dark:text-white">
              <span className="block">William</span>
              <span className="block">
                Sisulu{" "}
                <span className="editorial-italic text-[var(--brand-blue)] dark:text-[#8fb7ff]">
                  Odhiambo
                </span>
              </span>
            </h1>
          </div>
        </div>

        <div className="relative grid -translate-y-20 gap-8 border-t border-black/12 pt-6 text-left dark:border-white/14 md:grid-cols-[0.9fr_1.1fr_1fr] md:items-end md:-translate-y-28">
          <span className="brand-rule absolute left-0 right-0 top-0 h-px opacity-80" />

          <div className="space-y-2">
            {focusAreas.map((role) => (
              <p
                key={role}
                className="text-sm font-semibold uppercase tracking-[0.18em] text-[#242424] dark:text-white"
              >
                {role}
              </p>
            ))}
          </div>

          <p className="max-w-xl text-lg leading-tight tracking-[-0.035em] text-[#3b3b3b] dark:text-[#dfddd5] sm:text-2xl">
            Crafting technology that solves problems while safeguarding what
            matters most.
          </p>

          <div className="hidden flex-wrap gap-x-5 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6f6b63] dark:text-[#b9b4aa] sm:flex md:justify-end">
            {toolMarks.map((tool) => (
              <span key={tool}>{tool}</span>
            ))}
          </div>
        </div>
      </div>

      <a
        href="#quick-chat"
        aria-label="Scroll to quick chat"
        className="absolute bottom-8 left-1/2 z-10 hidden h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border border-black/12 text-[#242424] transition hover:border-black dark:border-white/14 dark:text-white dark:hover:border-white sm:flex"
      >
        <i className="bx bx-chevron-down text-2xl"></i>
      </a>
    </section>
  );
};

export default Hero;